import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { supabase } from "../../lib/supabase";
import Header from '../../components/ui/Header';
import QuoteCard from './components/QuoteCard';
import { Loader2, Send, ArrowLeft, ChevronDown, ChevronUp, DollarSign, Building2, Calendar, Plane, Coffee, Luggage, ShieldCheck, MapPin, Clock } from 'lucide-react';

export default function MessagesV2Conversation() {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const [messages, setMessages] = useState([]);
  const [conversation, setConversation] = useState(null);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [expandedQuoteDetails, setExpandedQuoteDetails] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (user?.id && conversationId) {
      fetchConversationAndMessages();
      subscribeToMessages();
    }
  }, [user, conversationId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef?.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchConversationAndMessages = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch conversation details with proper foreign key syntax
      const { data: conversationData, error: conversationError } = await supabase?.from("conversations")?.select(`
          *,
          enquiry:enquiries(destination, trip_type),
          agent:user_profiles!conversations_agent_id_fkey(full_name, company_name),
          customer:user_profiles!conversations_customer_id_fkey(full_name)
        `)?.eq("id", conversationId)?.single();

      if (conversationError) throw conversationError;
      setConversation(conversationData);

      // Fetch messages
      const { data: messagesData, error: messagesError } = await supabase?.from("messages")?.select(`
          *,
          sender:user_profiles!messages_sender_id_fkey(full_name)
        `)?.eq("conversation_id", conversationId)?.order("created_at", { ascending: true });

      if (messagesError) throw messagesError;
      setMessages(messagesData || []);
    } catch (err) {
      console.error("Error fetching conversation:", err);
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const subscribeToMessages = () => {
    const subscription = supabase?.channel(`conversation-${conversationId}`)?.on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "messages",
          filter: `conversation_id=eq.${conversationId}`,
        },
        async (payload) => {
          // Deduplicate: only add if not already present
          setMessages((prev) => {
            const exists = prev?.some(msg => msg?.id === payload?.new?.id);
            if (exists) return prev;
            
            // Fetch sender details for new message
            supabase?.from("user_profiles")?.select("full_name")?.eq("id", payload?.new?.sender_id)?.single()?.then(({ data: senderData }) => {
              const newMessage = {
                ...payload?.new,
                sender: senderData,
              };
              setMessages((current) => {
                const stillExists = current?.some(msg => msg?.id === newMessage?.id);
                if (stillExists) return current;
                return [...current, newMessage];
              });
            });
            
            return prev;
          });
        }
      )?.subscribe();

    return () => {
      subscription?.unsubscribe();
    };
  };

  const handleSendMessage = async (e) => {
    e?.preventDefault();
    if (!newMessage?.trim() || sending) return;

    try {
      setSending(true);
      setError(null);

      // Insert and get the message back immediately
      const { data: insertedMessage, error: sendError } = await supabase?.from("messages")?.insert({
        conversation_id: conversationId,
        sender_id: user?.id,
        content: newMessage?.trim(),
        sender_role: userProfile?.role || 'customer',
      })?.select(`
        *,
        sender:user_profiles!messages_sender_id_fkey(full_name)
      `)?.single();

      if (sendError) throw sendError;

      // Immediately append to local state for instant UI update (deduplicate by id)
      if (insertedMessage) {
        setMessages((prev) => {
          const exists = prev?.some(msg => msg?.id === insertedMessage?.id);
          if (exists) return prev;
          return [...prev, insertedMessage];
        });
      }

      // Clear input instantly
      setNewMessage("");
    } catch (err) {
      console.error("Error sending message:", err);
      setError(err?.message);
    } finally {
      setSending(false);
    }
  };

  const handleBack = () => {
    // Navigate back based on user role
    if (conversation?.enquiry_id) {
      navigate(`/customer/messages-v2/enquiry/${conversation?.enquiry_id}`);
    } else {
      navigate("/customer/messages-v2");
    }
  };

  const toggleQuoteDetails = () => {
    setExpandedQuoteDetails(!expandedQuoteDetails);
  };

  const handleAcceptQuote = async (quote) => {
    // TODO: Implement quote acceptance logic
    console.log('Accept quote:', quote);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-64px)]">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      {/* Conversation Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <button
            onClick={handleBack}
            className="text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            {conversation && (
              <>
                <h2 className="font-semibold text-gray-900">
                  {conversation?.metadata?.agentCompanyName || conversation?.agent?.company_name || '-'}
                  {(conversation?.metadata?.agentFullName || conversation?.agent?.full_name) && 
                    ` - ${conversation?.metadata?.agentFullName || conversation?.agent?.full_name}`
                  }
                </h2>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Quote Details Section - Expandable/Collapsible */}
      {conversation?.quote_price && (
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-4xl mx-auto">
            <button
              onClick={toggleQuoteDetails}
              className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-blue-600" />
                  <span className="font-semibold text-gray-900">
                    Quote: {conversation?.quote_currency} {conversation?.quote_price?.toLocaleString()}
                  </span>
                </div>
                {conversation?.quote_submitted_at && (
                  <span className="text-xs text-gray-500">
                    Sent {new Date(conversation.quote_submitted_at)?.toLocaleDateString('en-GB', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric'
                    })}
                  </span>
                )}
              </div>
              {expandedQuoteDetails ? (
                <ChevronUp className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              )}
            </button>

            {expandedQuoteDetails && conversation?.quote_metadata && (
              <div className="px-4 pb-4 space-y-4 border-t border-gray-100">
                {/* Hotel/Accommodation */}
                {conversation?.quote_metadata?.hotelName && (
                  <div className="pt-4">
                    <div className="flex items-start gap-3">
                      <Building2 className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-700">Accommodation</p>
                        <p className="text-base text-gray-900 mt-1">{conversation?.quote_metadata?.hotelName}</p>
                        {conversation?.quote_metadata?.boardBasis && (
                          <p className="text-sm text-gray-600 mt-1">
                            {conversation?.quote_metadata?.boardBasis?.replace(/_/g, ' ')?.replace(/\b\w/g, c => c?.toUpperCase())}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Dates */}
                {conversation?.quote_metadata?.dates && (
                  <div className="flex items-start gap-3">
                    <Calendar className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-700">Travel Dates</p>
                      {conversation?.quote_metadata?.dates?.flexible ? (
                        <p className="text-base text-gray-900 mt-1">Flexible dates</p>
                      ) : conversation?.quote_metadata?.dates?.startDate ? (
                        <p className="text-base text-gray-900 mt-1">
                          {new Date(conversation.quote_metadata.dates.startDate)?.toLocaleDateString('en-GB', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric'
                          })} - {new Date(conversation.quote_metadata.dates.endDate)?.toLocaleDateString('en-GB', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric'
                          })}
                        </p>
                      ) : null}
                    </div>
                  </div>
                )}

                {/* Flights */}
                {conversation?.quote_metadata?.flights?.included && (
                  <div className="flex items-start gap-3">
                    <Plane className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-700">Flights</p>
                      <p className="text-base text-gray-900 mt-1">Flights included</p>
                      {conversation?.quote_metadata?.flights?.departureAirport && (
                        <p className="text-sm text-gray-600 mt-1">
                          <MapPin className="w-3 h-3 inline mr-1" />
                          From: {conversation?.quote_metadata?.flights?.departureAirport}
                        </p>
                      )}
                      {conversation?.quote_metadata?.flights?.airline && (
                        <p className="text-sm text-gray-600">
                          Airline: {conversation?.quote_metadata?.flights?.airline}
                        </p>
                      )}
                      {conversation?.quote_metadata?.flights?.flightTimes && (
                        <p className="text-sm text-gray-600">
                          <Clock className="w-3 h-3 inline mr-1" />
                          {conversation?.quote_metadata?.flights?.flightTimes}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Inclusions Grid */}
                {(conversation?.quote_metadata?.transferIncluded || 
                  conversation?.quote_metadata?.baggageIncluded || 
                  conversation?.quote_metadata?.additionalInclusions) && (
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-3">What's Included</p>
                    <div className="grid grid-cols-2 gap-3">
                      {conversation?.quote_metadata?.transferIncluded && (
                        <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg border border-green-100">
                          <ShieldCheck className="w-5 h-5 text-green-600 flex-shrink-0" />
                          <span className="text-sm text-gray-900">Airport Transfer</span>
                        </div>
                      )}
                      {conversation?.quote_metadata?.baggageIncluded && (
                        <div className="flex items-center gap-2 p-3 bg-purple-50 rounded-lg border border-purple-100">
                          <Luggage className="w-5 h-5 text-purple-600 flex-shrink-0" />
                          <span className="text-sm text-gray-900">Baggage Allowance</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Additional Inclusions */}
                {conversation?.quote_metadata?.additionalInclusions && (
                  <div className="flex items-start gap-3">
                    <Coffee className="w-5 h-5 text-orange-500 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-700">Additional Inclusions</p>
                      <p className="text-sm text-gray-900 mt-1 whitespace-pre-wrap">
                        {conversation?.quote_metadata?.additionalInclusions}
                      </p>
                    </div>
                  </div>
                )}

                {/* Additional Notes */}
                {conversation?.quote_metadata?.notes && (
                  <div className="pt-3 border-t border-gray-100">
                    <p className="text-sm font-medium text-gray-700 mb-2">Additional Notes</p>
                    <p className="text-sm text-gray-600 whitespace-pre-wrap">
                      {conversation?.quote_metadata?.notes}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Messages Container */}
      <div className="flex-1 overflow-y-auto px-4 py-6">
        <div className="max-w-4xl mx-auto space-y-4">
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          )}

          {/* Quote Card - Display at top of conversation if quote exists */}
          {conversation?.quote_price && (
            <QuoteCard 
              quote={conversation} 
              onAcceptQuote={handleAcceptQuote}
            />
          )}

          {messages?.map((message) => {
            const isOwnMessage = message?.sender_id === user?.id;
            return (
              <div
                key={message?.id}
                className={`flex ${isOwnMessage ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[70%] rounded-lg px-4 py-3 ${
                    isOwnMessage
                      ? "bg-blue-600 text-white" :"bg-white text-gray-900 border border-gray-200"
                  }`}
                >
                  {!isOwnMessage && (
                    <p className="text-xs font-medium mb-1 opacity-75">
                      {message?.sender?.full_name}
                    </p>
                  )}
                  <p className="text-sm whitespace-pre-wrap">{message?.content}</p>
                  <p
                    className={`text-xs mt-1 ${
                      isOwnMessage ? "text-blue-100" : "text-gray-500"
                    }`}
                  >
                    {new Date(message.created_at)?.toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </p>
                </div>
              </div>
            );
          })}
          <div ref={messagesEndRef} />
        </div>
      </div>
      {/* Message Input */}
      <div className="bg-white border-t border-gray-200 px-4 py-4">
        <div className="max-w-4xl mx-auto">
          <form onSubmit={handleSendMessage} className="flex gap-2">
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e?.target?.value)}
              placeholder="Type your message..."
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={sending}
            />
            <button
              type="submit"
              disabled={!newMessage?.trim() || sending}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              {sending ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}